import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import { RxArrowDown } from "react-icons/rx";
import { DetailTableProps } from "types";
import { groupBy } from "helpers/groupBy";
import {
  CustomHeadCell,
  UnitHeaderCell,
} from "components/utils/LiquidationDetails";
import { arsFormatter } from "helpers";
import Row from "./StayDetailRow";
import { SETTLEMENT_TYPES } from "constants/index";
import { getAbonoUnitValue, isAbonoSettlement, liquidationPalletsUnitsTotal, liquidationStayUnitsTotal, liquidationVolumenUnitsTotal } from "helpers/liquidations";

export default function StayDetailTable({
  liquidation,
  print = false,
  chip,
  hasDetail,
}: DetailTableProps) {
  const theme = useTheme();

  const settlementType = useMemo(
    () => liquidation.liquidation_settlement_types[1],
    [liquidation],
  );

  const logsGroupedByDay = useMemo(() => {
    return groupBy(liquidation.liquidation_article_location_log, ["date"]);
  }, [liquidation]);

   const sortedLogsByDay = useMemo(() => {
    return Object.entries(logsGroupedByDay).sort((a, b) =>
      b[0].localeCompare(a[0]),
    );
  }, [logsGroupedByDay]);

  const isAbono = isAbonoSettlement(settlementType);

  const abonoUnitValue = getAbonoUnitValue(settlementType, sortedLogsByDay.length);

  const showMinimumAdjustmentColumn = useMemo(() => {
    const minimumValue = Number(settlementType.minimum_value ?? 0);

    if (isAbono || minimumValue <= 0) return false;

    return sortedLogsByDay.some(([, logsOfDay]) => {
      const quantity =
        settlementType.settlement_type === SETTLEMENT_TYPES.PALLETS
          ? liquidationPalletsUnitsTotal(settlementType, liquidation, logsOfDay)
          : settlementType.settlement_type === SETTLEMENT_TYPES.VOLUMEN
            ? liquidationVolumenUnitsTotal(settlementType, liquidation, logsOfDay)
            : liquidationStayUnitsTotal(settlementType, liquidation, logsOfDay);

      return minimumValue > Number(quantity ?? 0);
    });
  }, [isAbono, settlementType, liquidation, sortedLogsByDay]);

  return (
    <Grid item xs={12}>
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        defaultExpanded={print}
      >
        <AccordionSummary
          expandIcon={
            !print && (
              <RxArrowDown style={{ color: theme.palette.primary.main }} />
            )
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Detalle de Estadías</Typography>
            {chip && (
              <Chip
                color={chip.color}
                size="small"
                label={chip.label}
                icon={chip.icon}
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer component={Paper} elevation={4}>
            <Table size="small" aria-label="collapsible table">
              <TableHead
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                })}
              >
                <TableRow>
                  <CustomHeadCell></CustomHeadCell>
                  <CustomHeadCell>Fecha</CustomHeadCell>
                  {!isAbono && UnitHeaderCell(settlementType.settlement_type)}
                  {showMinimumAdjustmentColumn && (
                    <CustomHeadCell>Cantidad Facturable</CustomHeadCell>
                  )}
                  <CustomHeadCell>Subtotal</CustomHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogsByDay.map((log, index) => (
                  <Row
                    key={index}
                    date={log[0]}
                    logsOfDay={log[1]}
                    liquidation={liquidation}
                    settlementType={settlementType}
                    print={print}
                    hasDetail={hasDetail}
                    isAbono={isAbono}
                    abonoUnitValue={abonoUnitValue}
                    showMinimumAdjustmentColumn={showMinimumAdjustmentColumn}
                  />
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={13} align="right">
                    <Typography variant="h6">
                      Total Estadías: {arsFormatter(settlementType.subtotal)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
}
